import { useCallback } from "react"
import { Card } from "../../_entities/card"
import { Member } from "../../_entities/member"

export const useApiClient = () => {
  const login = useCallback(
    async (adminMemberId: string, password: string): Promise<void> => {
      await request("/internal/login/request", {
        adminMemberId: adminMemberId,
        password: password,
      })
    },
    []
  )

  const logout = useCallback(async (): Promise<void> => {
    await request("/internal/logout")
  }, [])

  const requestPasswordReset = useCallback(
    async (adminMemberId: string, mail: string): Promise<void> => {
      await request("/internal/password/request-reset/request", {
        adminMemberId: adminMemberId,
        mail: mail,
      })
    },
    []
  )

  const resetPassword = useCallback(
    async (
      resetToken: string,
      newPassword: string,
      confirmPassword: string
    ): Promise<void> => {
      await fetch("/internal/password/reset/request", {
        method: "POST",
        body: JSON.stringify({
          resetToken: resetToken,
          newPassword: newPassword,
          confirmPassword: confirmPassword,
        }),
        credentials: "same-origin",
      })
    },
    []
  )

  const request = async (path: string, body?: object): Promise<void> => {
    let bodyData = null
    if (body != null) {
      bodyData = JSON.stringify(body)
    }
    const res = await fetch(path, {
      method: "POST",
      body: bodyData,
      credentials: "same-origin",
    })
    if (res.status !== 200) {
      const body = await res.json()
      throw new Error(body.message)
    }
  }

  const findMemberByCardNumber = useCallback(
    async (cardNumber: string): Promise<{ member: Member }> => {
      const res = await fetch("/internal/member/findByCardNumber", {
        method: "POST",
        credentials: "same-origin",
        body: JSON.stringify({
          cardNumber: cardNumber,
        }),
      })
      const body = await res.json()
      if (res.status !== 200) {
        throw new Error(body.message)
      }
      return body
    },
    []
  )

  const findCardByMemberId = useCallback(
    async (invididualMemberId: string): Promise<{ card: Card | null }> => {
      const res = await fetch("/internal/card/findByMemberId", {
        method: "POST",
        credentials: "same-origin",
        body: JSON.stringify({
          invididualMemberId: invididualMemberId,
        }),
      })
      const body = await res.json()
      if (res.status !== 200) {
        throw new Error(body.message)
      }
      return body
    },
    []
  )

  const createCard = useCallback(
    async (invididualMemberId: string, nickname: string): Promise<void> => {
      const res = await fetch("/internal/card/create/request", {
        method: "POST",
        credentials: "same-origin",
        body: JSON.stringify({
          invididualMemberId: invididualMemberId,
          nickname: nickname,
        }),
      })
      if (res.status !== 200) {
        throw new Error("Creation failed")
      }
    },
    []
  )

  const updateCardNickname = useCallback(
    async (cardNumber: string, nickname: string): Promise<void> => {
      const res = await fetch("/internal/card/updateNickname", {
        method: "POST",
        credentials: "same-origin",
        body: JSON.stringify({
          cardNumber: cardNumber,
          nickname: nickname,
        }),
      })
      if (res.status !== 200) {
        throw new Error("Update failed")
      }
    },
    []
  )

  const resetCardPassword = useCallback(
    async (cardNumber: string): Promise<{ password: string }> => {
      const res = await fetch("/internal/card/resetPassword", {
        method: "POST",
        credentials: "same-origin",
        body: JSON.stringify({
          cardNumber: cardNumber,
        }),
      })
      if (res.status !== 200) {
        throw new Error("Reset failed")
      }
      return await res.json()
    },
    []
  )

  const deleteCard = useCallback(async (cardNumber: string): Promise<void> => {
    const res = await fetch("/internal/card/delete", {
      method: "POST",
      credentials: "same-origin",
      body: JSON.stringify({
        cardNumber: cardNumber,
      }),
    })
    if (res.status !== 200) {
      throw new Error("Delete failed")
    }
  }, [])

  const enableCard = useCallback(
    async (cardNumber: string): Promise<{ password: string }> => {
      const res = await fetch("/internal/card/enable", {
        method: "POST",
        credentials: "same-origin",
        body: JSON.stringify({
          cardNumber: cardNumber,
        }),
      })
      if (res.status !== 200) {
        throw new Error("Enable failed")
      }
      return await res.json()
    },
    []
  )
  const disableCard = useCallback(async (cardNumber: string): Promise<void> => {
    const res = await fetch("/internal/card/disable", {
      method: "POST",
      credentials: "same-origin",
      body: JSON.stringify({
        cardNumber: cardNumber,
      }),
    })
    if (res.status !== 200) {
      throw new Error("Disable failed")
    }
  }, [])

  return {
    login,
    logout,
    findMemberByCardNumber,
    findCardByMemberId,
    createCard,
    requestPasswordReset,
    resetPassword,
    updateCardNickname,
    resetCardPassword,
    deleteCard,
    enableCard,
    disableCard,
  }
}
